import React from 'react';
import { Plus, Storefront, TrendDown, TrendUp } from '@phosphor-icons/react';

import { Icon } from '../../design-system/components/Icon';
import { MarketShell } from '../../design-system/components/shells';
import {
  Card,
  Delta,
  Button,
  PageHeader,
  Segmented,
  Stat,
  Switch,
  SwitchRow,
  Tabs,
} from '../../design-system/components/desktop';
import { ConsoleStack, Notice, StatGrid, StatValue } from '../../design-system/patterns/console';
import { useViewport } from '../../design-system/hooks/useViewport';
import { formatCurrency } from '../../utils/common';

import '../Dashboard/dashboard.css';

/**
 * `/dashboard` for an approved seller. The view. Pure.
 *
 * THE NESTED TAB PROBLEM, AND THE ANSWER
 * --------------------------------------
 * This page had TWO tab bars stacked on each other: an outer Seller View /
 * Buyer View pair, and an inner Transactions / My Listings / Orders row. Two
 * underline rows one above the other is unreadable, and `.dsk-tabs` is a single
 * row by design, so one of them had to stop being a tab bar.
 *
 * The OUTER pair is a `Segmented` in the page header. Which side of the
 * marketplace you are looking at is a COMMITTED MODE, not navigation within a
 * page: it changes what the whole page is about, including the KPI band, the
 * primary action and the set of inner tabs. `Segmented` fills the chosen
 * segment with INK, which is the system's mark for a choice the user made, and
 * it reports itself with `aria-pressed` rather than `aria-selected`, so a
 * screen reader is not told there are two tab lists on one page.
 *
 * The INNER row is the only real `Tabs`: those genuinely are panels of one
 * screen, and they get the accent underline, which is the system's mark for
 * state. One tab bar, and a mode reads as a mode.
 *
 * WHAT IS NOT HERE
 * ----------------
 * No courier, no origin city and no shipping price appears anywhere on this
 * page, because we cannot pull that data. The old `ChartSection` and its four
 * gradient legend swatches, the `ProfitLoss` recharts block with its
 * `#2465EA -> #08B0D5` bar gradient, and `TransactionSection` were all dead
 * code (nothing imported them) and went with the conversion.
 *
 * The 1,654-line `SellerDashboard.css`, `Views.css`, `tabs.css`, the hand
 * rolled `Tabe.jsx`, `Layout.jsx` with its dead Tailwind classes and the whole
 * `NavigationHeader` tree are replaced by the shared `MarketShell` chrome.
 */

const SELLER_TABS = [
  { id: 'transactions', label: 'Transactions' },
  { id: 'listings', label: 'My listings' },
  { id: 'orders', label: 'Orders' },
];

const BUYER_TABS = [
  { id: 'saved', label: 'Collection' },
  { id: 'order_history', label: 'Order history' },
];

export const SELLER_DASHBOARD_TABS = SELLER_TABS;
export const BUYER_DASHBOARD_TABS = BUYER_TABS;

const MODES = [
  { value: 'seller_view', label: 'Seller view' },
  { value: 'buyer_view', label: 'Buyer view' },
];

/**
 * One KPI tile. `trend` is the API's own word and it is the ONLY thing that
 * decides the direction; the percentage string is rendered as it arrives.
 * Reading a sign out of a formatted string is how the old header ended up
 * claiming "Active Listings" was falling while it loaded.
 *
 * THREE STATES, NOT TWO. This used to be `const up = trend === 'increase'`,
 * so every word that was not "increase" meant DOWN. The API paired that with
 * `"increase" if this >= last`, which put a green up-arrow on all four tiles
 * of a store with nothing in it. The API now reports "flat" for no movement,
 * and treating that as a fourth kind of "not increase" would render it as a
 * red down-arrow -- worse than the bug it replaced. A flat tile gets no arrow
 * and no direction, so `Delta` leaves it untinted, which is the honest result
 * for a movement of zero.
 */
function MetricTile({ label, value, change, trend, loading }) {
  const direction = trend === 'increase' ? 'up' : trend === 'decrease' ? 'down' : undefined;
  const Arrow = direction === 'up' ? TrendUp : direction === 'down' ? TrendDown : null;
  return (
    <Stat label={label} value={<StatValue loading={loading}>{value}</StatValue>}>
      {loading || change === undefined || change === null || change === '' ? null : (
        <Delta direction={direction}>
          {Arrow ? <Icon as={Arrow} size="sm" active /> : null}
          {change} <span className="dsk-hint">from last month</span>
        </Delta>
      )}
    </Stat>
  );
}

export function SellerDashboardView({
  mode = 'seller_view',
  onModeChange = () => {},
  tab = 'transactions',
  onTabChange = () => {},

  metrics = null,
  metricsLoading = false,

  storeUnavailable = false,
  availabilityBusy = false,
  onAvailabilityChange = () => {},

  reviews = null,
  buyerStats = null,
  panel = null,

  onNewListing,
  onExploreMarketplace,
}) {
  const seller = mode === 'seller_view';
  const tabs = seller ? SELLER_TABS : BUYER_TABS;
  /* Only for the breadcrumb/top-bar decision below; this component still mounts
     one tree, and `MarketShell` does its own branching for the chrome. */
  const { isDesktop } = useViewport();

  // The one page-level command follows the mode, because the mode is what the
  // page is for. It sits in the page header beside the mode switch, not in a
  // utility bar: there is no utility bar any more, because there is no console.
  // The icon is not decoration: this is the one command on the page, and a
  // glyph is what makes it findable at a glance among four stat cards and a
  // tab strip that are all text.
  const primaryAction = seller
    ? { label: 'New listing', icon: Plus, onClick: onNewListing }
    : { label: 'Explore marketplace', icon: Storefront, onClick: onExploreMarketplace };

  /* MarketShell, NOT ConsoleShell, and this is the information-architecture
     correction Junaid asked for.

     The dashboard was mounted in a console with its own left sidebar, which
     made it read as a separate application you crossed into rather than a
     section of Sleevr. It is not a separate flow: a seller moves between the
     marketplace and their dashboard constantly, and the sidebar duplicated
     the top-level nav badly while hiding it.

     So the dashboard now wears the same chrome as everything else a signed in
     user touches, and Dashboard is a link in the primary nav instead. The
     admin console keeps `ConsoleShell` — that genuinely is a separate internal
     tool with its own nav, and it is the only thing the console was for. */
  /* THE "FLOATING DASHBOARD TEXT ABOVE THE DASHBOARD HEADER" WAS THE TOP BAR.
     This page said "Dashboard" twice on a phone. `MarketShell` hands `title`
     to `MobileTopBar`, which renders it as `.ds-topbar__title` — 18px,
     centred, in a bar that is `position: sticky; top: 0` with a sticky
     z-index, so it hovers over the page as you scroll. Directly under it the
     `PageHeader` below renders the same word again as the 28px `<h1>`. Two
     "Dashboard"s stacked, the upper one literally floating: exactly what
     Junaid described, and why a search for a stray positioned element found
     nothing — neither piece is stray on its own.

     THE H1 IS THE ONE THAT STAYS. He named the lower one "the dashboard
     header" and pointed at the thing above it, so the header is what he wants
     left standing. It is also the copy that carries the sub line under it.

     Killing the top-bar copy takes BOTH props, not just `title`: MarketShell
     falls back to the last breadcrumb label when `title` is absent, which is
     "Dashboard" again. With neither, its `wordmark={!title && !breadcrumbs}`
     test flips and the bar shows the Sleevr wordmark, which is what the app's
     other tab-bar destinations show. Nothing else is lost — `/dashboard` is in
     the shell's SECTION_ROOTS, so it never derived a back control from that
     crumb, and a one-crumb trail names no parent anyway.

     Desktop keeps the trail: `MarketShell` ignores `title` entirely above 1024
     and renders breadcrumbs only, so the duplication never existed there and
     removing the crumb would only cost the trail.

     NOTE: `BuyerDashboard.view.jsx` and `Dashboard.view.jsx` have the identical
     pair and are not this agent's files to change. Marketplace.view.jsx solves
     the same clash the other way — it keeps the top-bar title and drops the
     PageHeader below 1024 — because its mobile page is a dense filter-and-grid
     surface with no room for a 28px h1. This one has the room. */
  return (
    <MarketShell
      breadcrumbs={isDesktop ? [{ label: 'Dashboard' }] : []}
      showSearch={false}
      activeNav="dashboard"
    >
      <ConsoleStack>
        {seller && storeUnavailable ? (
          <Notice title="Your store is marked unavailable.">
            Buyers see an unavailability notice instead of your listings, and no new orders can be
            placed until you switch it back.
          </Notice>
        ) : null}

        <PageHeader
          title="Dashboard"
          sub={
            seller
              ? 'Your sales, your listings and the orders you owe.'
              : 'What you have saved and what you have bought.'
          }
          actions={
            <>
              <Segmented
                label="Dashboard mode"
                value={mode}
                onChange={onModeChange}
                items={MODES}
              />
              <Button onClick={primaryAction.onClick}>
                <Icon as={primaryAction.icon} />
                {primaryAction.label}
              </Button>
            </>
          }
        />

        {seller ? (
          <>
            <StatGrid>
              <MetricTile
                label="Total revenue"
                value={metrics ? formatCurrency(metrics.totalRevenue.value) : 'No data'}
                change={metrics ? `${metrics.totalRevenue.change}%` : undefined}
                trend={metrics?.totalRevenue.trend}
                loading={metricsLoading}
              />
              <MetricTile
                label="Active listings"
                value={metrics ? metrics.activeListings.value : 'No data'}
                change={metrics ? `${metrics.activeListings.change}%` : undefined}
                trend={metrics?.activeListings.trend}
                loading={metricsLoading}
              />
              <MetricTile
                label="Total orders"
                value={metrics ? metrics.totalOrders.value : 'No data'}
                change={metrics ? `${metrics.totalOrders.change}%` : undefined}
                trend={metrics?.totalOrders.trend}
                loading={metricsLoading}
              />
              {/* A never-rated store has `value: null`, not 0 -- a bare 0 on
                  this tile reads as customers rating the store zero, which is
                  a claim the data never made. The movement is suppressed with
                  it: there is nothing for it to be a movement in. And the
                  figure carries a '%' like the other three, because it is the
                  same `change_percent` field they render. */}
              <MetricTile
                label="Customer rating"
                value={
                  !metrics ? 'No data'
                    : metrics.customerRating.value === null ? 'No ratings yet'
                      : metrics.customerRating.value
                }
                change={
                  metrics && metrics.customerRating.value !== null
                    ? `${metrics.customerRating.change}%`
                    : undefined
                }
                trend={metrics?.customerRating.trend}
                loading={metricsLoading}
              />
            </StatGrid>

            {/* The availability switch commits immediately, which is why it is
                not next to a save button. It is a store control, so it sits
                with the store's numbers rather than in the page title row,
                where it would compete with the mode choice. */}
            <Card>
              <SwitchRow
                title="Store availability"
                sub={
                  storeUnavailable
                    ? 'Your store is hidden from buyers. Switch this on to start selling again.'
                    : 'Your listings are visible and buyers can order from you.'
                }
              >
                <Switch
                  checked={!storeUnavailable}
                  disabled={availabilityBusy}
                  onChange={(event) => onAvailabilityChange(!event.target.checked)}
                />
              </SwitchRow>
            </Card>

            <div className="dsa-dash__band">{reviews}</div>
          </>
        ) : (
          /* Buyer mode gets the BUYER's numbers, not a blank space above the
             tabs and not the seller's revenue tiles. A seller looking at their
             own collection is a buyer for as long as they are looking, and the
             same band renders on the dedicated buyer dashboard. */
          buyerStats
        )}

        <div>
          <Tabs
            tabs={tabs}
            value={tab}
            onChange={onTabChange}
            label={seller ? 'Seller dashboard sections' : 'Buyer dashboard sections'}
          />
          <div className="dsa-dash__panel">{panel}</div>
        </div>
      </ConsoleStack>
    </MarketShell>
  );
}

export default SellerDashboardView;
